import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

/**
 * Each BottomSheet registers itself here so the stack can drive its nesting transform.
 */
interface SheetHandle {
  /** Apply scale + translateY to this sheet's paper element */
  applyNestingTransform: (scale: number, translateY: number, withTransition: boolean) => void;
}

interface BottomSheetNestingContextValue {
  /**
   * Called by a BottomSheet when it opens. Returns a cleanup function to call when it closes.
   * The stack automatically scales down the sheet below.
   */
  push: (handle: SheetHandle) => () => void;
  /** Called by the topmost sheet during drag to interpolate the sheet below */
  notifyDrag: (percentageDragged: number) => void;
  /** Called by the topmost sheet on drag release */
  notifyRelease: (isStillOpen: boolean) => void;
}

const NO_OP_CONTEXT: BottomSheetNestingContextValue = {
  push: () => () => {},
  notifyDrag: () => {},
  notifyRelease: () => {},
};

const BottomSheetNestingContext = createContext<BottomSheetNestingContextValue>(NO_OP_CONTEXT);

export function useBottomSheetNestingContext(): BottomSheetNestingContextValue {
  return useContext(BottomSheetNestingContext);
}

/** How far the parent sheet shifts up when a nested child opens (px) */
const NESTED_DISPLACEMENT = 16;
/** Offset for the background wrapper when first sheet opens */
const BACKGROUND_OFFSET = 14;
const BACKGROUND_BORDER_RADIUS = 8;
const TRANSITION = "500ms cubic-bezier(0.32, 0.72, 0, 1)";

function getScaledDown(): number {
  return (window.innerWidth - NESTED_DISPLACEMENT) / window.innerWidth;
}

function getBackgroundScale(): number {
  return (window.innerWidth - 26) / window.innerWidth;
}

/** Finds the page wrapper element for background scale effect */
function getBackgroundWrapper(): HTMLElement | null {
  return document.querySelector("[data-bottom-sheet-wrapper]");
}

function setBackgroundTransform(
  wrapper: HTMLElement,
  scale: number,
  translateY: number,
  borderRadius: number,
  withTransition: boolean
): void {
  wrapper.style.transition = withTransition
    ? `transform ${TRANSITION}, border-radius ${TRANSITION}`
    : "none";
  wrapper.style.borderRadius = `${borderRadius}px`;
  wrapper.style.overflow = "hidden";

  wrapper.style.transform =
    scale === 1 && translateY === 0 ? "none" : `scale(${scale}) translate3d(0, ${translateY}px, 0)`;
}

/**
 * Wrap BottomSheets in this provider for automatic nested drawer behavior.
 * No props needed on individual sheets — the stack is managed here.
 *
 * Also scales the page background (element with `data-bottom-sheet-wrapper`)
 * when the first sheet opens, creating a recessed card effect.
 */
export function BottomSheetNestingProvider(props: { children: ReactNode }) {
  const stackRef = useRef<SheetHandle[]>([]);
  const bgRestoreTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // --- Background scale helpers ---
  const scaleBackgroundDown = useCallback((withTransition: boolean) => {
    const wrapper = getBackgroundWrapper();
    if (!wrapper) {
      return;
    }

    if (bgRestoreTimerRef.current) {
      clearTimeout(bgRestoreTimerRef.current);
    }

    document.body.style.background = "black";
    setBackgroundTransform(
      wrapper,
      getBackgroundScale(),
      BACKGROUND_OFFSET,
      BACKGROUND_BORDER_RADIUS,
      withTransition
    );
  }, []);

  const scaleBackgroundUp = useCallback((withTransition: boolean) => {
    const wrapper = getBackgroundWrapper();
    if (!wrapper) {
      return;
    }

    setBackgroundTransform(wrapper, 1, 0, 0, withTransition);

    // Restore body background after transition completes
    bgRestoreTimerRef.current = setTimeout(() => {
      document.body.style.removeProperty("background");
    }, 500);
  }, []);

  const interpolateBackground = useCallback((percentageDragged: number) => {
    const wrapper = getBackgroundWrapper();
    if (!wrapper) {
      return;
    }

    const bgScale = getBackgroundScale();
    const currentScale = bgScale + percentageDragged * (1 - bgScale);
    const currentY = BACKGROUND_OFFSET - percentageDragged * BACKGROUND_OFFSET;
    const currentRadius = BACKGROUND_BORDER_RADIUS - percentageDragged * BACKGROUND_BORDER_RADIUS;
    setBackgroundTransform(wrapper, currentScale, currentY, currentRadius, false);
  }, []);

  // --- Sheet nesting helpers ---
  const scaleDown = useCallback((handle: SheetHandle) => {
    const s = getScaledDown();
    handle.applyNestingTransform(s, -NESTED_DISPLACEMENT, true);
  }, []);

  const scaleUp = useCallback((handle: SheetHandle) => {
    handle.applyNestingTransform(1, 0, true);
  }, []);

  const push = useCallback(
    (handle: SheetHandle) => {
      const stack = stackRef.current;
      const wasEmpty = stack.length === 0;

      // Scale down the sheet that was previously on top
      const below = stack.at(-1);
      if (below) {
        scaleDown(below);
      }

      stack.push(handle);

      // First sheet opened — scale the page background down
      if (wasEmpty) {
        scaleBackgroundDown(true);
      }

      // Cleanup: called when this sheet closes
      return () => {
        const index = stack.indexOf(handle);
        if (index === -1) {
          return;
        }

        stack.splice(index, 1);

        // If the removed sheet was on top, restore the new top sheet
        if (index === stack.length) {
          const newTop = stack.at(-1);
          if (newTop) {
            scaleUp(newTop);
          }
        }

        // Stack is now empty — restore the page background
        if (stack.length === 0) {
          scaleBackgroundUp(true);
        }
      };
    },
    [scaleDown, scaleUp, scaleBackgroundDown, scaleBackgroundUp]
  );

  const notifyDrag = useCallback(
    (percentageDragged: number) => {
      const stack = stackRef.current;

      // If only one sheet and it's being dragged, interpolate the background
      if (stack.length === 1) {
        interpolateBackground(percentageDragged);
        return;
      }

      if (stack.length < 2) {
        return;
      }

      // The sheet below the topmost one gets interpolated
      const below = stack.at(-2);
      if (!below) {
        return;
      }

      const s = getScaledDown();
      const currentScale = s + percentageDragged * (1 - s);
      const currentTranslateY = -NESTED_DISPLACEMENT + percentageDragged * NESTED_DISPLACEMENT;
      below.applyNestingTransform(currentScale, currentTranslateY, false);
    },
    [interpolateBackground]
  );

  const notifyRelease = useCallback(
    (isStillOpen: boolean) => {
      const stack = stackRef.current;

      // Single sheet releasing — snap background
      if (stack.length === 1) {
        if (isStillOpen) {
          scaleBackgroundDown(true);
        } else {
          scaleBackgroundUp(true);
        }
        return;
      }

      if (stack.length < 2) {
        return;
      }

      const below = stack.at(-2);
      if (!below) {
        return;
      }

      if (isStillOpen) {
        scaleDown(below);
      } else {
        scaleUp(below);
      }
    },
    [scaleDown, scaleUp, scaleBackgroundDown, scaleBackgroundUp]
  );

  // Clean up background restore timer on unmount
  useEffect(() => {
    return () => {
      if (bgRestoreTimerRef.current) {
        clearTimeout(bgRestoreTimerRef.current);
      }
    };
  }, []);

  const value = useMemo<BottomSheetNestingContextValue>(
    () => ({ push, notifyDrag, notifyRelease }),
    [push, notifyDrag, notifyRelease]
  );

  return (
    <BottomSheetNestingContext.Provider value={value}>
      {props.children}
    </BottomSheetNestingContext.Provider>
  );
}
